import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { api, apiEvents, ApiError } from "@/lib/api";
import { SCHEMA } from "@/lib/schema";
import { TABLE_KEYS, type ActionContext, type ActionResult, type BoardSummary, type CrmRecord, type Invoice, type Me, type Status, type TableKey, type Tables } from "@/lib/types";

// De gegevens van de app (vroeger store.js). De frontend laadt alle CRM-records in één keer
// (GET /api/data, contract §6.2) en rekent er zelf mee: lijsten, filters, dashboard, zoeken.
// Wijzigingen gaan altijd via de server; het antwoord bevat de volledige nieuwe records, die hier
// in de lijst gezet worden zonder alles opnieuw te laden.

type Phase = "loading" | "anonymous" | "ready" | "error";

const emptyTables = () => Object.fromEntries(TABLE_KEYS.map((key) => [key, []])) as unknown as Tables;

interface CrmState {
  phase: Phase;
  error: string | null;
  me: Me | null;
  status: Status | null;
  tables: Tables;
  invoices: Invoice[];
  /** Eigen borden voor het menu (§6.9), zonder de gearchiveerde. */
  boards: BoardSummary[];
  fetchedAt: string | null;
}

export interface Crm extends CrmState {
  /** Alles opnieuw laden (knop Vernieuwen, na aanmelden). */
  reload: () => Promise<void>;
  /** Records uit een serverantwoord in de lijsten zetten. */
  applyRecords: (records: { table: TableKey; record: CrmRecord }[]) => void;
  setInvoices: (update: (list: Invoice[]) => Invoice[]) => void;
  /** De lijst met borden opnieuw ophalen (na aanmaken, hernoemen, archiveren of verplaatsen). */
  reloadBoards: () => Promise<void>;
  /** Teller van één bord in het menu bijwerken zonder alles opnieuw te laden. */
  setBoardCount: (boardId: string, cardCount: number) => void;
  /** Een actie uitvoeren (POST /api/actions/{id}, §6.3). */
  runAction: (actionId: string, input: Record<string, unknown>, context: Partial<ActionContext>) => Promise<ActionResult>;
  /** Een record aanmaken of corrigeren (Bewerken, §6.4). */
  saveRecord: (table: TableKey, id: string | null, fields: Record<string, unknown>) => Promise<CrmRecord>;
  logout: () => Promise<void>;
  /** Heeft de aangemelde gebruiker dit recht? Alleen om knoppen te verbergen; de server controleert opnieuw. */
  can: (permission: string) => boolean;
  all: (table: TableKey) => CrmRecord[];
  get: (table: TableKey, id: string | null | undefined) => CrmRecord | null;
  /** Records uit `table` waarvan koppelveld `field` naar `id` wijst. */
  linked: (table: TableKey, field: string, id: string | null | undefined) => CrmRecord[];
  /** Naam (primair veld) van een record. */
  name: (table: TableKey, id: string | null | undefined) => string;
  names: (table: TableKey, ids: unknown) => string;
}

const CrmContext = createContext<Crm | null>(null);

export function CrmProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<CrmState>({ phase: "loading", error: null, me: null, status: null, tables: emptyTables(), invoices: [], boards: [], fetchedAt: null });

  const reload = useCallback(async () => {
    try {
      const me = await api<Me>("auth/me", { quiet401: true });
      if (me.mustSetup2fa) {
        // Beheerder zonder verplichte 2FA: alleen Mijn account tot die ingesteld is (§6.1).
        setState((s) => ({ ...s, phase: "ready", error: null, me, tables: emptyTables(), invoices: [], boards: [] }));
        return;
      }
      const [status, data] = await Promise.all([api<Status>("status"), api<{ tables: Tables; fetchedAt: string }>("data")]);
      const invoices = me.permissions.includes("facturen.lezen") ? (await api<{ items: Invoice[] }>("invoices")).items : [];
      const boards = (await api<{ items: BoardSummary[] }>("boards")).items;
      const tables = emptyTables();
      for (const key of TABLE_KEYS) tables[key] = data.tables[key] ?? [];
      setState({ phase: "ready", error: null, me, status, tables, invoices, boards, fetchedAt: data.fetchedAt });
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) setState((s) => ({ ...s, phase: "anonymous", me: null, tables: emptyTables(), invoices: [], boards: [] }));
      else setState((s) => ({ ...s, phase: "error", error: error instanceof Error ? error.message : String(error) }));
    }
  }, []);

  useEffect(() => {
    apiEvents.onUnauthenticated = () => setState((s) => ({ ...s, phase: "anonymous", me: null }));
    apiEvents.onTwoFactorRequired = () => setState((s) => (s.me ? { ...s, me: { ...s.me, mustSetup2fa: true } } : s));
    void reload();
  }, [reload]);

  const applyRecords = useCallback((records: { table: TableKey; record: CrmRecord }[]) => {
    setState((s) => {
      const tables = { ...s.tables };
      for (const { table, record } of records) {
        const list = tables[table];
        tables[table] = list.some((r) => r.id === record.id) ? list.map((r) => (r.id === record.id ? record : r)) : [...list, record];
      }
      return { ...s, tables };
    });
  }, []);

  const crm = useMemo<Crm>(() => {
    const { tables, me } = state;
    const all = (table: TableKey) => tables[table];
    const get = (table: TableKey, id: string | null | undefined) => (id ? tables[table].find((r) => r.id === id) ?? null : null);
    const name = (table: TableKey, id: string | null | undefined) => {
      const record = get(table, id);
      if (!record) return "";
      const value = record.fields[SCHEMA.tables[table].primary];
      return value === undefined ? "(naamloos)" : String(value);
    };
    return {
      ...state,
      reload,
      applyRecords,
      setInvoices: (update) => setState((s) => ({ ...s, invoices: update(s.invoices) })),
      reloadBoards: async () => {
        const { items } = await api<{ items: BoardSummary[] }>("boards");
        setState((s) => ({ ...s, boards: items }));
      },
      // Zonder wijziging dezelfde state teruggeven: geen nieuwe render (BoardPage roept dit in een effect op).
      setBoardCount: (boardId, cardCount) =>
        setState((s) => (s.boards.some((b) => b.id === boardId && b.cardCount !== cardCount) ? { ...s, boards: s.boards.map((b) => (b.id === boardId ? { ...b, cardCount } : b)) } : s)),
      runAction: async (actionId, input, context) => {
        const result = await api<ActionResult>(`actions/${encodeURIComponent(actionId)}`, { body: { input, context } });
        applyRecords(result.records);
        return result;
      },
      saveRecord: async (table, id, fields) => {
        const record = id
          ? await api<CrmRecord>(`records/${table}/${id}`, { method: "PATCH", body: { fields } })
          : await api<CrmRecord>(`records/${table}`, { method: "POST", body: { fields } });
        applyRecords([{ table, record }]);
        return record;
      },
      logout: async () => {
        await api("auth/logout", { method: "POST" }).catch(() => {});
        setState({ phase: "anonymous", error: null, me: null, status: null, tables: emptyTables(), invoices: [], boards: [], fetchedAt: null });
      },
      can: (permission) => Boolean(me?.permissions.includes(permission)),
      all,
      get,
      linked: (table, field, id) => (id ? tables[table].filter((r) => Array.isArray(r.fields[field]) && (r.fields[field] as string[]).includes(id)) : []),
      name,
      names: (table, list) => (Array.isArray(list) ? list.map((id) => name(table, String(id))).filter(Boolean).join(", ") : "")
    };
  }, [state, reload, applyRecords]);

  return <CrmContext.Provider value={crm}>{children}</CrmContext.Provider>;
}

export function useCrm(): Crm {
  const crm = useContext(CrmContext);
  if (!crm) throw new Error("useCrm buiten CrmProvider");
  return crm;
}
